import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatNZD } from "@/lib/format";
import { toast } from "sonner";
import {
  Car, Search as SearchIcon, Loader2, ShieldCheck, ExternalLink, Sparkles, Rocket,
  CheckCircle2, XCircle,
} from "lucide-react";

/**
 * Car Valuation freemium page.
 * Spec: CLASSIFIEDS_CAR_VALUATION_CONNECTION_KIT
 * - GET /car-valuation/quota
 * - GET /car-valuation/{plate}
 * - POST /car-valuation/checkout (opens Stripe)
 * - Success page polls /checkout/status/{id}
 */

const PACKS = [
  { qty: 1, label: "1 valuation" },
  { qty: 5, label: "5 valuations" },
  { qty: 10, label: "10 valuations" },
];

export default function CarValuationPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialPlate = (sp.get("plate") || "").toUpperCase();
  const [plate, setPlate] = useState(initialPlate);
  const [submitted, setSubmitted] = useState(initialPlate || null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [payQty, setPayQty] = useState(1);

  const quotaQ = useQuery({
    queryKey: ["car-valuation-quota", user?.id],
    queryFn: async () => (await sharedApi.get("/classifieds/car-valuation/quota")).data,
    enabled: !!user,
    retry: 0,
  });

  const valuationQ = useQuery({
    queryKey: ["car-valuation", submitted],
    queryFn: async () => (await sharedApi.get(`/classifieds/car-valuation/${encodeURIComponent(submitted)}`)).data,
    enabled: !!submitted && !!user,
    retry: 0,
  });

  // 402 → open paywall
  useEffect(() => {
    if (!valuationQ.error) return;
    const err = valuationQ.error;
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 402 && detail && typeof detail === "object" && detail.code === "payment_required") {
      setShowPaywall(true);
    } else if (status === 422 && typeof detail === "string") {
      toast.error(detail);
    } else if (status === 400) {
      toast.error("Enter a valid NZ plate.");
    }
  }, [valuationQ.error]);

  const checkoutMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/car-valuation/checkout", {
        quantity: payQty,
        origin_url: window.location.origin,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        try {
          sessionStorage.setItem(
            "car_valuation_pending",
            JSON.stringify({ plate: submitted, quantity: payQty }),
          );
        } catch { /* ignore */ }
        window.location.href = data.url;
      } else {
        toast.error("Couldn't open checkout — please try again.");
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Checkout failed"),
  });

  const submit = (e) => {
    e.preventDefault();
    const p = plate.trim().toUpperCase().replace(/\s+/g, "");
    if (p.length < 3) { toast.error("Enter a valid plate."); return; }
    setPlate(p);
    setSubmitted(p);
    setShowPaywall(false);
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Car className="w-10 h-10 text-[color:var(--brand-blue)] mx-auto" />
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">Sign in to value your car</h1>
        <p className="mt-2 text-slate-500 max-w-sm mx-auto">Get 3 free valuations when you sign in.</p>
        <button
          type="button"
          onClick={() => nav(`/login?next=${encodeURIComponent("/car-valuation")}`)}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
        >
          Sign in
        </button>
      </div>
    );
  }

  const quota = quotaQ.data;
  const v = valuationQ.data;
  const pending = v?.pending;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Car Valuation — Allsale" description="Get a fair market range for your car in seconds. 3 free lookups." />
      <Breadcrumbs items={[{ label: "Car Valuation" }]} />

      <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#0F70A8] via-slate-900 to-slate-900 text-white p-6 md:p-10 shadow-card" data-testid="car-valuation-hero">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> Market valuation
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-5xl font-black leading-tight">What&apos;s your car worth?</h1>
        <p className="mt-2 text-white/70 max-w-lg">Enter your NZ number plate — we&apos;ll show you a fair market range with matching variants.</p>

        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="car-valuation-form">
          <label className="flex-1 relative">
            <SearchIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={plate}
              data-testid="car-valuation-plate-input"
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="e.g. KRB300"
              maxLength={8}
              className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white text-slate-900 tabular-nums text-xl font-black tracking-widest text-center focus:outline-none focus:ring-4 focus:ring-white/25"
            />
          </label>
          <button
            type="submit"
            data-testid="car-valuation-submit"
            disabled={valuationQ.isLoading}
            className="rounded-2xl bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-3 transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
          >
            {valuationQ.isFetching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Car className="w-5 h-5" />}
            Value my car
          </button>
        </form>

        {quota && (
          <div className="mt-4 text-xs text-white/70" data-testid="car-valuation-quota">
            {quota.free_remaining > 0 ? (
              <>{quota.free_remaining} free {quota.free_remaining === 1 ? "valuation" : "valuations"} remaining · then ${formatMoney(quota.price_nzd)} per lookup{quota.paid_credits ? ` · ${quota.paid_credits} paid credit${quota.paid_credits === 1 ? "" : "s"} in your account` : ""}</>
            ) : quota.paid_credits > 0 ? (
              <>{quota.paid_credits} paid credit{quota.paid_credits === 1 ? "" : "s"} available · $ {formatMoney(quota.price_nzd)} per additional lookup</>
            ) : (
              <>Free valuations used · $ {formatMoney(quota.price_nzd)} per lookup</>
            )}
          </div>
        )}
      </div>

      {/* Result */}
      {submitted && (
        <div className="mt-8">
          {valuationQ.isLoading && (
            <div className="rounded-2xl bg-white shadow-card p-10 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="mt-3 text-sm">Fetching valuation for {submitted}…</p>
            </div>
          )}

          {v && pending && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-8 text-center" data-testid="car-valuation-pending">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />
              <h3 className="mt-3 font-display text-lg font-bold text-slate-900">Preparing valuation…</h3>
              <p className="mt-2 text-sm text-slate-600">This can take up to 30 seconds — nothing was charged.</p>
              <button
                type="button"
                onClick={() => valuationQ.refetch()}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2 transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {v && !pending && v.summary && (
            <ValuationResult data={v} />
          )}
        </div>
      )}

      {/* Paywall */}
      <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
        <DialogContent className="max-w-md" data-testid="car-valuation-paywall">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-slate-900">
              You&apos;ve used your 3 free valuations
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Buy prepaid credits — each credit values one plate ($ {formatMoney(quota?.price_nzd || 1.5)}).
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid grid-cols-3 gap-2">
            {PACKS.map((p) => (
              <button
                key={p.qty}
                type="button"
                onClick={() => setPayQty(p.qty)}
                data-testid={`car-valuation-pack-${p.qty}`}
                className={`rounded-xl p-3 text-center border-2 transition-all ${payQty === p.qty ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)]/5" : "border-soft bg-white hover:border-[color:var(--brand-blue)]"}`}
              >
                <div className="font-display text-xl font-black text-slate-900">
                  ${formatMoney(p.qty * (quota?.price_nzd || 1.5))}
                </div>
                <div className="text-xs text-slate-500">{p.label}</div>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => checkoutMut.mutate()}
            disabled={checkoutMut.isPending}
            data-testid="car-valuation-checkout-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 transition-colors disabled:opacity-70"
          >
            {checkoutMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Continue to secure payment
          </button>

          <p className="mt-2 text-[11px] text-slate-400 text-center">
            Test mode — use card 4242 4242 4242 4242 with any future expiry &amp; CVC.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ValuationResult({ data }) {
  const summary = data.summary || {};
  const variants = data.variants || [];
  const vehicle = data.vehicle || {};
  return (
    <div className="rounded-2xl bg-white shadow-card p-6 md:p-8" data-testid="car-valuation-result">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fair market range</div>
          <div className="mt-1 font-display text-3xl md:text-4xl font-black text-slate-900 tabular-nums">
            {formatNZD(summary.low)} <span className="text-slate-400">–</span> {formatNZD(summary.high)}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {vehicle.year} {vehicle.make} {vehicle.model}
            {vehicle.odometer_km ? ` · ${new Intl.NumberFormat("en-NZ").format(vehicle.odometer_km)} km` : ""}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
          data.charged === "paid"
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : data.charged === "free"
            ? "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]"
            : "bg-slate-100 text-slate-600"
        }`}>
          <ShieldCheck className="w-3 h-3" />
          {data.charged === "paid" ? "Paid" : data.charged === "free" ? "Free" : "Cached"}
        </span>
      </div>

      {variants.length > 0 && (
        <div className="mt-6 overflow-x-auto -mx-6 md:mx-0" data-testid="car-valuation-variants">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-soft text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <th className="py-2 pl-6 md:pl-0">Model</th>
                <th className="py-2">Trans</th>
                <th className="py-2 text-right">New price</th>
                <th className="py-2 text-right pr-6 md:pr-0">Now worth</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((row, i) => (
                <tr key={i} className="border-b border-subtle last:border-0">
                  <td className="py-2 pl-6 md:pl-0 font-semibold text-slate-800">
                    {row.year} {row.make} {row.model}
                  </td>
                  <td className="py-2 text-slate-600">{row.trans}</td>
                  <td className="py-2 text-right text-slate-600 tabular-nums">
                    {row.new_price ? formatNZD(row.new_price) : "—"}
                  </td>
                  <td className="py-2 pr-6 md:pr-0 text-right font-bold text-slate-900 tabular-nums">
                    {formatNZD(row.dealer_price_kms_adjusted || row.dealer_price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {/* Iter-356 founder directive — CTA copy is "List your car in 60
            seconds" (the word "sell" must not appear). Route + params
            unchanged from Iter-352. */}
        <Link
          to={(() => {
            const p = new URLSearchParams({ prefill: "vehicle" });
            if (data.plate) p.set("plate", data.plate);
            if (vehicle.year) p.set("year", vehicle.year);
            if (vehicle.make) p.set("make", vehicle.make);
            if (vehicle.model) p.set("model", vehicle.model);
            if (vehicle.odometer_km) p.set("odometer_km", vehicle.odometer_km);
            if (summary.low) p.set("value_low", summary.low);
            if (summary.high) p.set("value_high", summary.high);
            return `/post-ad?${p.toString()}`;
          })()}
          data-testid="cv-sell-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold text-sm px-5 py-2.5 transition-colors"
        >
          <Rocket className="w-4 h-4" /> List your car in 60 seconds — ad pre-filled from this plate
        </Link>
      </div>

      <p className="mt-4 text-[11px] text-slate-400">Estimates based on public NZ market data. Actual sale prices may vary.</p>
    </div>
  );
}

function formatMoney(n) {
  if (!Number.isFinite(Number(n))) return "0.00";
  return Number(n).toFixed(2).replace(/\.00$/, ".00");
}
